import { Controller } from "@hotwired/stimulus"

// Bulk selection on the LV tree: row checkboxes (with shift-click ranges) drive
// a selection bar with bulk edit/delete forms; the selected ids are injected
// into those forms as item_ids[] right before submit.
//
// A Titel stands for everything beneath it: ticking it ticks its rows, and it shows
// checked / indeterminate from the positions below it. The forms receive the selection
// compacted — a fully ticked Titel as its own id, without the rows beneath it — so
// unticking one row under a Titel drops exactly that row, and a whole Titel stays one id.
// Rows find their Titel through the tree markup (boq/items/_node: a [data-kind] node,
// a group's rows inside its [data-group-id] subtree) — or, in the flat position grid
// (boq/position_grid/_grid), through each row's `data-parent-id`.
export default class extends Controller {
  static targets = ["checkbox", "bar", "count", "editPanel"]
  // Ids to tick on load: the selection a price request form hands back to the tree.
  static values = { preselected: Array }

  connect() {
    this.lastChecked = null
    const preselected = new Set(this.preselectedValue.map(String))
    this.checkboxTargets.forEach((box) => {
      if (preselected.has(box.dataset.id)) box.checked = true
    })
    this.checkboxTargets.filter((box) => box.checked).forEach((box) => this.cascade(box))
    this.refresh()
  }

  // A row streamed back after a cell save (the grid replaces the row and its Titel) arrives
  // unticked. Turbo's replace removes the old row and inserts the new one in one mutation, so the
  // new box takes over the state of the box it replaced; the hand-over is cleared right after.
  checkboxTargetDisconnected(box) {
    if (!box.checked && box !== this.lastChecked) return
    this.replaced ||= new Map()
    if (this.replaced.size === 0) queueMicrotask(() => this.replaced.clear())
    this.replaced.set(box.dataset.id, { checked: box.checked, last: box === this.lastChecked })
  }

  checkboxTargetConnected(box) {
    const previous = this.replaced?.get(box.dataset.id)
    if (!previous) return
    this.replaced.delete(box.dataset.id)
    box.checked = previous.checked
    if (previous.last) this.lastChecked = box
    this.refresh()
  }

  toggle(event) {
    const checkbox = event.target
    let changed = [checkbox]
    if (event.shiftKey && this.lastChecked && this.lastChecked !== checkbox) {
      const boxes = this.checkboxTargets
      const start = boxes.indexOf(this.lastChecked)
      const end = boxes.indexOf(checkbox)
      changed = this.onScreen(boxes.slice(Math.min(start, end), Math.max(start, end) + 1))
      changed.forEach((box) => (box.checked = checkbox.checked))
    }
    changed.forEach((box) => this.cascade(box))
    this.lastChecked = checkbox
    this.refresh()
  }

  refresh() {
    this.syncGroups()
    const any = this.checkboxTargets.some((box) => box.checked)
    if (this.hasBarTarget) this.barTarget.classList.toggle("hidden", !any)
    if (this.hasCountTarget) this.countTarget.textContent = this.selectedCount()
    if (!any && this.hasEditPanelTarget) this.editPanelTarget.classList.add("hidden")
  }

  toggleEditPanel() {
    this.editPanelTarget.classList.toggle("hidden")
  }

  // Unticks every row and lets refresh() close the bar. lastChecked is reset so the
  // next shift-click does not span a range from an already-cleared row.
  clearSelection() {
    this.checkboxTargets.forEach((box) => {
      box.checked = false
      box.indeterminate = false
    })
    this.lastChecked = null
    this.refresh()
  }

  // Hand the current selection to the save-to-catalog modal. The modal lives outside
  // #boq_tree (so it survives tree re-renders), so this goes through a document event
  // rather than reaching into its DOM.
  openCatalogModal() {
    const ids = this.selectedIds()
    if (ids.length === 0) return

    document.dispatchEvent(new CustomEvent("catalog-save:open", { detail: { ids, name: "" } }))
  }

  injectIds(event) {
    const ids = this.selectedIds()
    if (ids.length === 0) {
      event.preventDefault()
      return
    }
    const form = event.target
    form.querySelectorAll("input[name='item_ids[]']").forEach((el) => el.remove())
    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "item_ids[]"
      input.value = id
      form.appendChild(input)
    })
  }

  // Same as injectIds, but as one comma-separated field for GET forms: a long selection
  // as item_ids[] parameters would overflow the URL.
  injectSelection(event) {
    const ids = this.selectedIds()
    if (ids.length === 0) {
      event.preventDefault()
      return
    }
    event.target.querySelector("input[name='selection']").value = ids.join(",")
  }

  // Checked rows, without those beneath a checked Titel (the Titel stands for them).
  selectedIds() {
    const checked = new Set(this.checkboxTargets.filter((box) => box.checked))
    const boxesById = this.boxesById()
    return [...checked]
      .filter(
        (box) => !this.ancestorBoxes(box, boxesById).some((ancestor) => checked.has(ancestor)),
      )
      .map((box) => box.dataset.id)
  }

  // What the bar counts: ticked positions, plus ticked Titel that have none beneath them.
  selectedCount() {
    return this.checkboxTargets.filter(
      (box) => box.checked && this.descendantBoxes(box).every((child) => this.isGroup(child)),
    ).length
  }

  // A Titel hands its state down to every row beneath it that is on screen or only folded away —
  // not to rows the grid's search or filters left out: the bulk bar would delete what nobody saw.
  // The Titel then reads as partly ticked and is handed over as its ticked rows, not as itself.
  cascade(box) {
    if (!this.isGroup(box)) return
    this.descendantBoxes(box)
      .filter((child) => !this.filteredOut(child))
      .forEach((child) => {
        child.checked = box.checked
        child.indeterminate = false
      })
  }

  // A Titel with positions beneath it is checked when all of them are, indeterminate
  // when some are. Without positions it keeps its own state.
  syncGroups() {
    this.checkboxTargets
      .filter((box) => this.isGroup(box))
      .forEach((group) => {
        const positions = this.descendantBoxes(group).filter((child) => !this.isGroup(child))
        if (positions.length === 0) {
          group.indeterminate = false
          return
        }
        const checked = positions.filter((child) => child.checked).length
        group.checked = checked === positions.length
        group.indeterminate = checked > 0 && checked < positions.length
      })
  }

  isGroup(box) {
    return this.nodeOf(box)?.dataset.kind === "group"
  }

  nodeOf(box) {
    return box.closest("[data-kind]")
  }

  descendantBoxes(box) {
    const node = this.nodeOf(box)
    if (this.flat(node)) return this.flatDescendantBoxes(node)
    const subtree = node?.querySelector(`:scope > [data-group-id="${node.dataset.id}"]`)
    if (!subtree) return []
    return [...subtree.querySelectorAll(this.checkboxSelector)]
  }

  ancestorBoxes(box, boxesById) {
    const ancestors = []
    if (this.flat(this.nodeOf(box))) {
      for (let id = this.nodeOf(box).dataset.parentId; id; id = this.rowOf(id)?.dataset.parentId) {
        const ancestor = boxesById.get(id)
        if (ancestor) ancestors.push(ancestor)
      }
      return ancestors
    }
    let node = this.nodeOf(box)?.parentElement?.closest("[data-kind]")
    while (node) {
      const ancestor = boxesById.get(node.dataset.id)
      if (ancestor) ancestors.push(ancestor)
      node = node.parentElement?.closest("[data-kind]")
    }
    return ancestors
  }

  boxesById() {
    return new Map(this.checkboxTargets.map((box) => [box.dataset.id, box]))
  }

  // By identifier: billing-offer-bulk extends this controller.
  get checkboxSelector() {
    return `[data-${this.identifier}-target~='checkbox']`
  }

  // -- the keyboard (the position grid) -----------------------------------------------------

  // Shift+Space ticks the selected row as a click would — a Titel with the rows beneath it — and
  // Shift+↑/↓ right after it widens or narrows the ticked run over the rows on screen, as Shift+click
  // does. Claimed through grid-cells' `command`, before it would type the space; the arrow itself is
  // left to grid-cells, which moves the selection on. Any other key ends the run: the next
  // Shift+arrow is a plain move again. On a Ja/Nein cell Space stays the cell's own, Shift or not.
  command(event) {
    const { originalEvent: key, rowId, column } = event.detail
    const run = this.keyRun
    this.keyRun = null
    if (!key.shiftKey || key.ctrlKey || key.metaKey || key.altKey) return

    const row = this.rowOf(rowId)
    const box = row?.querySelector(this.checkboxSelector)
    if (!box) return

    if (key.key === " ") {
      if (row.querySelector(`td[data-column="${column}"] input[type=checkbox]`)) return

      event.preventDefault()
      box.checked = !box.checked
      this.toggle({ target: box, shiftKey: false })
      // Every box as the run found it: rows it lets go of again go back to that.
      const before = new Map(this.checkboxTargets.map((other) => [other, other.checked]))
      this.keyRun = { anchor: box, at: box, checked: box.checked, before }
    } else if ((key.key === "ArrowDown" || key.key === "ArrowUp") && run?.at === box) {
      this.keyRun = run
      this.extendKeyRun(key.key === "ArrowDown" ? 1 : -1)
    }
  }

  extendKeyRun(step) {
    const run = this.keyRun
    const onScreen = this.onScreen(this.checkboxTargets)
    const next = onScreen[onScreen.indexOf(run.at) + step]
    if (!next) return

    const from = onScreen.indexOf(run.anchor)
    const to = onScreen.indexOf(next)
    run.before.forEach((checked, box) => (box.checked = checked))
    onScreen.slice(Math.min(from, to), Math.max(from, to) + 1).forEach((box) => {
      box.checked = run.checked
      this.cascade(box)
    })
    run.at = next
    this.lastChecked = run.anchor
    this.refresh()
  }

  // Skips rows hidden by the client-side tree filter — a range must not silently select rows the
  // user cannot see.
  onScreen(boxes) {
    return boxes.filter((box) => box.offsetParent !== null)
  }

  // -- the flat grid: rows in tree pre-order, `data-parent-id` instead of nesting ---------------

  flat(node) {
    return node?.matches("tr[data-grid-row]")
  }

  // The grid hides folded and filtered rows alike and says which by its own attribute. The
  // row editor's trees (boq-tree, billing-offer-tree) fold a Titel's subtree container but hide a
  // filtered node itself.
  filteredOut(box) {
    const node = this.nodeOf(box)
    if (!this.flat(node)) return node?.classList.contains("hidden") ?? false
    return node.hidden && this.element.hasAttribute("data-position-tree-filtered")
  }

  rowOf(id) {
    return this.element.querySelector(`tr[data-grid-row][data-row-id="${id}"]`)
  }

  // Pre-order: a Titel's rows follow it until the next row that is not below it.
  flatDescendantBoxes(row) {
    const below = new Set([row.dataset.rowId])
    const boxes = []
    for (
      let next = row.nextElementSibling;
      next?.matches("tr[data-grid-row]");
      next = next.nextElementSibling
    ) {
      if (!below.has(next.dataset.parentId)) break
      below.add(next.dataset.rowId)
      const box = next.querySelector(this.checkboxSelector)
      if (box) boxes.push(box)
    }
    return boxes
  }
}
